import React from 'react';

import type { IntlShape } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import type { AiSuggestionsPlugin } from './aiSuggestionsPluginType';
import { aiSuggestionsPluginKey, createPlugin } from './pm-plugins/main';
import { SuggestionCardContentComponent } from './ui/components/SuggestionCard/SuggestionCardContentComponent';
import { updateSuggestions } from './ui/utils/updateSuggestions';

export const aiSuggestionsPlugin: AiSuggestionsPlugin = ({ api, config }) => {
	const { contentAri } = config;

	const quickInsert = ({
		formatMessage,
	}: {
		formatMessage: IntlShape['formatMessage'];
	}): QuickInsertItem[] =>
		contentAri
			? [
					{
						title: formatMessage(aiSuggestionsMessages.fetchGenerateFromDocument),
						keywords: ['ai', 'suggest', 'rovo'],
						icon: () => <AiGenerativeTextIcon label="" />,
						action(_insert, state) {
							updateSuggestions({
								api,
								contentAri,
								generate: true,
								forceAdf: false,
							});
							return state.tr;
						},
					},
				]
			: [
					{
						title: formatMessage(aiSuggestionsMessages.fetchGenerateFromAdf),
						keywords: ['ai', 'suggest', 'page', 'adf'],
						icon: () => <AiGenerativeTextIcon label="" />,
						action(_insert, state) {
							updateSuggestions({
								api,
								contentAri,
								generate: true,
								forceAdf: true,
							});
							return state.tr;
						},
					},
				];

	return {
		name: 'aiSuggestions',
		getSharedState(editorState: EditorState | undefined) {
			if (!editorState) {
				return undefined;
			}
			const pluginState = aiSuggestionsPluginKey.getState(editorState);
			if (!pluginState) {
				return undefined;
			}
			return {
				selectedSuggestion: pluginState.selectedSuggestion,
				suggestionById: pluginState.suggestionById,
				baseLocalIdBySuggestionId: pluginState.baseLocalIdBySuggestionId,
				suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
				orderedSuggestionIds: pluginState.orderedSuggestionIds,
			};
		},
		pmPlugins() {
			return [
				{
					name: 'aiSuggestionsPlugin',
					plugin: ({ getIntl, nodeViewPortalProviderAPI }) => {
						const intl = getIntl?.();
						return createPlugin({ api, intl, contentAri, nodeViewPortalProviderAPI });
					},
				},
			];
		},
		contentComponent({}) {
			return <SuggestionCardContentComponent api={api} />;
		},
		pluginsOptions: {
			quickInsert,
		},
	};
};
